import type { MockTestSummary } from '../../api/mockTest.types';
import { WEB_ICO } from '../../shell/icons';

export type PathNodeState = 'done' | 'current' | 'open';

export interface PathNodeData {
  id: string;
  /** "Unit 0.5" / "V-Unit 1" — nhãn nhỏ phía trên tên unit. */
  label: string;
  name: string;
  /** Glyph tiếng Nhật hiển thị khi chưa xong (あ/ア/部/語/文...). */
  jp: string;
  /** Mô tả ngắn hiển thị khi trạng thái là "open" (vd "8 bài"). */
  meta: string;
  state: PathNodeState;
  /** 0-100, chỉ có ý nghĩa khi state === 'current'. */
  pct: number;
}

const LOOK: Record<PathNodeState, { fill: string; glyph: string; ring: string; track: string }> = {
  done: { fill: 'var(--success)', glyph: '#fff', ring: 'var(--success)', track: 'var(--success)' },
  current: { fill: 'var(--surface-page)', glyph: 'var(--cta-default)', ring: 'var(--cta-default)', track: 'var(--progress-track)' },
  open: { fill: 'var(--surface-page)', glyph: 'var(--navy-300)', ring: 'var(--navy-300)', track: 'var(--navy-300)' },
};

export function PathRing({ size, stroke, pct, color, track }: { size: number; stroke: number; pct: number; color: string; track: string }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} style={{ position: 'absolute', left: 0, top: 0, transform: 'rotate(-90deg)' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
      {pct > 0 && (
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={`${(c * pct) / 100} ${c}`} />
      )}
    </svg>
  );
}

/**
 * Node tròn trên Lộ trình — vòng tiến độ + glyph/checkmark.
 * KHÔNG có trạng thái khoá: mọi unit đều mở, người học tự chọn thứ tự.
 */
export function PathNode({ unit, size = 64, onOpen }: { unit: PathNodeData; size?: number; onOpen: (u: PathNodeData) => void }) {
  const look = LOOK[unit.state];
  const ring = size + 12;
  // Luôn hiện % để theo dõi tiến độ ở mọi trạng thái — kể cả "open" (0%) và
  // "done" (100%), không chỉ riêng "current" như trước.
  const metaText = `${unit.pct}%`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <button
        onClick={() => onOpen(unit)}
        style={{ position: 'relative', width: ring, height: ring, padding: 0, border: 'none', background: 'none', cursor: 'pointer', flexShrink: 0 }}
      >
        <PathRing size={ring} stroke={5} pct={unit.state === 'done' ? 100 : unit.pct} color={look.ring} track={look.track} />
        <span
          style={{
            position: 'absolute',
            left: 6,
            top: 6,
            width: size,
            height: size,
            borderRadius: '50%',
            background: look.fill,
            border: unit.state === 'current' || unit.state === 'open' ? '1px solid var(--border-default)' : 'none',
            boxShadow: unit.state === 'current' ? 'var(--shadow-card)' : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--font-jp)',
            fontSize: size * 0.44,
            fontWeight: 500,
            color: look.glyph,
          }}
        >
          {unit.state === 'done' ? WEB_ICO.check('#fff', size * 0.42) : unit.jp}
        </span>
      </button>
      <div style={{ textAlign: 'center', maxWidth: 150 }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--navy-300)' }}>{unit.label}</div>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink)', lineHeight: 1.25 }}>{unit.name}</div>
        <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2, lineHeight: 1.3 }}>{metaText}</div>
      </div>
    </div>
  );
}

/** Đường nối dọc giữa hai node — xanh nếu node trước đã Hoàn thành. */
export function PathConnector({ done, height = 26 }: { done: boolean; height?: number }) {
  return <div style={{ width: 3, height, borderRadius: 2, background: done ? 'var(--success)' : 'var(--border-default)', margin: '0 auto' }} />;
}

const BRACKET_STROKE = 3;

/**
 * Ngoặc nối 3 nhánh (Từ vựng/Ngữ pháp/Bài tập) với thẻ "Bài test": `in` gom 3 cột về
 * giữa (ngay trên thẻ), `out` toả lại 3 cột (ngay dưới thẻ). Tâm cột tính bằng calc theo
 * cùng lưới 3 cột `gap` với hàng node nên luôn thẳng hàng ở mọi bề ngang.
 */
export function TestBracket({ kind, done, gap }: { kind: 'in' | 'out'; done: boolean; gap: number }) {
  const color = done ? 'var(--success)' : 'var(--border-default)';
  const isIn = kind === 'in';
  const arm = 24;
  const stem = 24;
  const border = `${BRACKET_STROKE}px solid ${color}`;
  return (
    <div style={{ position: 'relative', height: arm + stem }} aria-hidden>
      <div
        style={{
          position: 'absolute',
          left: `calc((100% - ${gap * 2}px) / 6 - ${BRACKET_STROKE / 2}px)`,
          width: `calc((100% - ${gap * 2}px) * 2 / 3 + ${gap * 2 + BRACKET_STROKE}px)`,
          top: isIn ? 0 : stem,
          height: arm,
          borderLeft: border,
          borderRight: border,
          borderBottom: isIn ? border : 'none',
          borderTop: isIn ? 'none' : border,
          borderRadius: isIn ? '0 0 14px 14px' : '14px 14px 0 0',
        }}
      />
      <div style={{ position: 'absolute', left: `calc(50% - ${BRACKET_STROKE / 2}px)`, top: 0, bottom: 0, width: BRACKET_STROKE, borderRadius: 2, background: color }} />
    </div>
  );
}

/** Thẻ "Bài test" sau mỗi 5 Bài — rộng hết hàng, khác node tròn của từng nhánh. */
export function MockTestCard({ test, onOpen }: { test: MockTestSummary; onOpen: () => void }) {
  const meta =
    test.bestScore != null
      ? `Cao nhất ${test.bestScore}/180 · ${test.bestPassed ? 'Đạt' : 'Chưa đạt'} · ${test.attemptCount} lần`
      : `${test.questionCount} câu · ${test.partQuestionCounts.length} phần · Có xếp hạng`;
  return (
    <button
      onClick={onOpen}
      aria-label={`Bài test ${test.title}`}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: 16,
        textAlign: 'left',
        borderRadius: 'var(--radius-card)',
        border: `1px solid ${test.bestPassed ? 'var(--success)' : 'var(--border-default)'}`,
        background: 'rgba(179,205,224,0.2)',
        cursor: 'pointer',
        fontFamily: 'var(--font-vi)',
      }}
    >
      <span
        style={{
          width: 52,
          height: 52,
          flexShrink: 0,
          borderRadius: '50%',
          background: test.bestPassed ? 'var(--success)' : 'var(--navy-500)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--font-jp)',
          fontSize: 24,
        }}
      >
        {test.bestPassed ? WEB_ICO.check('#fff', 24) : '試'}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--navy-300)' }}>Bài test</span>
        <span style={{ display: 'block', fontSize: 16.5, fontWeight: 700, color: 'var(--ink)' }}>{test.title}</span>
        <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{meta}</span>
      </span>
      {WEB_ICO.chev('var(--cta-default)', 18)}
    </button>
  );
}
